// Listing modes for Pilot Timesheets — Register / Weekly Matrix / Team / Reports / Codes.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`.
//
//   <base>/list    → TimesheetsDataGrid (the timesheets register)
//   <base>/week    → TimesheetsWeek     (the weekly matrix editor)
//   <base>/team    → TimesheetsTeam     (the payroll list: people × week, OPS Patch 4)
//   <base>/report  → TimesheetsReport   (period report / export)
//   <base>/codes   → TimesheetsCodes    (duty/pay codes reference)

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";
import type { ModeGates } from "../app/navGates";

export const MODE_LABELS: { [segment: string]: string } = {
  list: "List",
  week: "Week",
  team: "Team",
  report: "Report",
  codes: "Codes",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  list: "Weekly timesheets",
  week: "Hours, flying and expenses",
  team: "Payroll review by week",
  report: "Code and crew hours",
  codes: "Activity codes",
};

/**
 * Modes the Pilots group does not see (H131, Aaron 2026-09-28: "Pilot group
 * permissions remove these" — Team, Report, Codes). `notPilotsGroup` (H172,
 * "Hide for the Pilots group only"): a Pilots-group member without the
 * flightduty view_all_pilots token loses them; office staff keep them whatever
 * they hold. A pilot's Report was only ever their own hours; it goes from the
 * menu with the rest. Team's DATA still needs the token server-side
 * (app/timesheets/api.py `_can_view_all_crew`).
 * Read by the top nav, TimesheetsLayout and index.tsx — PATTERNS.md BP-90.
 */
export const MODE_GATES: ModeGates = {
  team: "notPilotsGroup",
  report: "notPilotsGroup",
  codes: "notPilotsGroup",
};

export const DEFAULT_MODE_SEGMENT = "list";

export const MODE_STORAGE_KEY = "timesheets.viewMode";

export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const parts = pathname.split("/").filter(Boolean);
  const modeCandidate = parts[parts.length - 1] || "";
  const onMode = isModeSegment(modeCandidate);
  const currentMode = onMode ? modeCandidate : readRememberedModeSegment();
  const basePath = onMode
    ? "/" + parts.slice(0, -1).join("/")
    : "/" + parts.join("/");
  return { basePath, currentMode, onMode };
}
